import { APIStatus } from "@/api/APIStatus.type";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { SquarePlayer } from "@/components/molecules/Player/SquarePlayer/SquarePlayer";
import { GAME_ASSET_SLUG } from "@/config/game";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { Pagination } from "@/design-system/Pagination/Pagination";
import { trackSort as trackMyClubSort } from "@/events/myClubMixpanel";
import { ClubEmptyState } from "@/features/MyClub/EmptyClub/ClubEmptyState";
import {
  convertPlayerPrice,
  convertSBCPrice,
} from "@/functions/convertPlayerPrice";
import { getPriceDisplay } from "@/functions/getPriceDisplay";
import { cn } from "@/lib/utils";
import {
  contentLabelsSelector,
  eventTokensSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { clubIsEmptySelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { SortBy, SortDirection } from "@repo/shared/redux";
import { useClubImportFeature } from "@repo/shared/redux";
import { ArrowDown, Check } from "lucide-react";
import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useMediaQuery } from "usehooks-ts";

const ASSETS = import.meta.env.VITE_APP_ASSETS_BASE_URL;

const PROPERTIES: {
  key: keyof Pick<
    SearchedPlayer,
    | "activeSquad"
    | "untradeable"
    | "transferList"
    | "duplicate"
    | "sbcStorage"
    | "manuallyAdded"
  >;
  icon: string;
}[] = [
  { key: "activeSquad", icon: "active-squad" },
  { key: "untradeable", icon: "untradeable" },
  { key: "transferList", icon: "transfer-list" },
  { key: "duplicate", icon: "duplicate" },
  { key: "sbcStorage", icon: "sbc-storage" },
  { key: "manuallyAdded", icon: "manually-added" },
];

type SortButtonProps = {
  by: SortBy;
  sortBy: SortBy;
  sortDirection: SortDirection;
  onSort: (by: SortBy, direction: SortDirection) => void;
  children: ReactNode;
};

/** Sorts the table by one column; tapping it again flips the order. */
const SortButton = ({
  by,
  sortBy,
  sortDirection,
  onSort,
  children,
}: SortButtonProps) => {
  const active = sortBy === by;
  const asc = active && sortDirection === "asc";
  return (
    <button
      type="button"
      onClick={() => {
        onSort(by, active && sortDirection === "desc" ? "asc" : "desc");
        if (by === "profit")
          trackMyClubSort({ subsection: "My Players", value: "profit" });
      }}
      className={cn(
        "inline-flex items-center gap-1 uppercase transition-colors",
        active ? "text-neutral-50" : "hover:text-neutral-200",
      )}
    >
      <span className="flex h-4 items-center">{children}</span>
      {/* One arrow that turns, so flipping the order animates. */}
      <ArrowDown
        className={cn(
          "size-3.5 transition-[rotate,opacity] duration-200",
          active ? "opacity-100" : "opacity-40",
          asc && "rotate-180",
        )}
      />
    </button>
  );
};

const SortHeader = ({
  className,
  ...props
}: SortButtonProps & { className?: string }) => (
  <th className={cn("px-2 py-3 mobile:px-3", className)}>
    <SortButton {...props} />
  </th>
);

/**
 * Every player in the club. Built to read what the club is worth: price and
 * profit are the loudest columns, rating and position sit quieter beside
 * them, and the properties (active squad, untradeable, …) are icons.
 * Selecting rows (to remove them) replaces opening the player.
 */
export const PlayerTable = ({
  fetchPlayersStatus,
  players,
  page,
  setPage,
  totalAmountOfPages,
  sortBy,
  sortDirection,
  onSort,
  isSelecting,
  selectedPlayers,
  handleSelectPlayer,
}: {
  fetchPlayersStatus: APIStatus;
  players: SearchedPlayer[];
  page: number;
  setPage: (page: number) => void;
  totalAmountOfPages: number;
  sortBy: SortBy;
  sortDirection: SortDirection;
  onSort: (by: SortBy, direction: SortDirection) => void;
  isSelecting: boolean;
  selectedPlayers: SearchedPlayer[];
  handleSelectPlayer: (player: SearchedPlayer, isSelected: boolean) => void;
}) => {
  const { t } = useTranslation(["myClub", "common"]);
  const eventTokens = useSelector(eventTokensSelector);
  const { positions } = useSelector(contentLabelsSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const navigate = useNavigate();
  const clubIsEmpty = useSelector(clubIsEmptySelector);
  const clubAccess = useClubImportFeature("see-own-players");
  /*
    No club at all — logged out, or never imported. The page never fires the
    fetch in that case, so the status stays idle and the emptiness has to be
    read off the club instead of off the response.
  */
  const clubMissing = !clubAccess.allowed && clubAccess.reason !== "loading";
  const fetchSettled =
    fetchPlayersStatus === "fulfilled" || fetchPlayersStatus === "rejected";
  const noPlayers = !players.length && fetchSettled;
  const loading = fetchPlayersStatus === "pending";

  if (clubMissing || (noPlayers && clubIsEmpty))
    // An empty club replaces the table outright: the table chrome frames rows
    // that will never come, and reads as a broken layout.
    return (
      <div className="px-4 py-6 mobile:px-6">
        <ClubEmptyState context="my-players" section="My Club" />
      </div>
    );

  const sortProps = { sortBy, sortDirection, onSort };

  return (
    <div className="flex w-full flex-col">
      <div>
        <table className="w-full table-fixed text-left text-sm">
          <thead className="bg-neutral-800 text-xs font-semibold tracking-wider text-foreground-muted">
            <tr>
              <th className="w-2/5 px-4 py-3 uppercase mobile:w-3/10 mobile:px-6">
                {/* Phones: the rating sits on the photo, so this column sorts by it. */}
                {isMobile ? (
                  <SortButton by="rating" {...sortProps}>
                    {t("common:rating")}
                  </SortButton>
                ) : (
                  <span>{t("players.table.player")}</span>
                )}
              </th>
              <SortHeader
                by="price"
                className="w-1/5 text-right mobile:w-3/20"
                {...sortProps}
              >
                {t("players.table.price")}
              </SortHeader>
              <SortHeader
                by="profit"
                className="w-1/5 text-right mobile:w-3/20"
                {...sortProps}
              >
                {t("players.table.profit")}
              </SortHeader>
              <SortHeader
                by="itemScore"
                className="w-1/5 pr-4 text-right mobile:w-1/10 mobile:pr-3"
                {...sortProps}
              >
                {isMobile ? (
                  <span
                    className="flex items-center"
                    aria-label={t("fodder.table.itemScore")}
                  >
                    <ItemScoreDiamond size={12} />
                  </span>
                ) : (
                  <span>{t("fodder.table.itemScore")}</span>
                )}
              </SortHeader>
              <SortHeader
                by="rating"
                className="hidden mobile:table-cell mobile:w-1/12"
                {...sortProps}
              >
                {t("common:rating")}
              </SortHeader>
              <th className="hidden w-1/12 px-3 py-3 uppercase mobile:table-cell">
                {t("players.table.position")}
              </th>
              <th className="hidden px-3 py-3 uppercase mobile:table-cell">
                {t("players.table.properties")}
              </th>
            </tr>
          </thead>
          <tbody
            className={cn(
              "transition-opacity",
              loading && players.length > 0 && "opacity-60",
            )}
          >
            {noPlayers ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-12 text-center text-foreground-muted"
                >
                  {t("players.noPlayersFound")}
                </td>
              </tr>
            ) : (
              players.map((player) => {
                const { iconUrl, label, showNumber, displayPrice } =
                  getPriceDisplay(player.priceInfo, eventTokens);
                const isSelected = selectedPlayers.some(
                  (p) => p.eaPlayerId === player.eaPlayerId,
                );
                const open = () => {
                  if (isSelecting) {
                    handleSelectPlayer(player, isSelected);
                    return;
                  }
                  navigate(
                    generateNavigationUrl({
                      resourceId: player.resourceId,
                      name: player.displayName,
                      evolutionPath: player.evolutionPath,
                      pathwayPath: player.pathwayPath,
                      evoPathId: player.evoPathId,
                      canEvolve: player.canEvolve,
                      basePath: "/players",
                    }),
                  );
                };
                return (
                  <tr
                    key={`${player.eaPlayerId}-${player.resourceId}`}
                    onClick={open}
                    aria-selected={isSelecting ? isSelected : undefined}
                    className={cn(
                      "group cursor-pointer border-t border-neutral-800 transition-colors",
                      isSelected
                        ? "bg-blue-950/40"
                        : "bg-neutral-900 hover:bg-neutral-800/70",
                    )}
                  >
                    {/* Phones: the photo runs the row's full height, edge to edge. */}
                    <td className="py-0 pr-2 mobile:px-6 mobile:py-2">
                      <div className="flex min-w-0 items-center gap-2 mobile:gap-2.5">
                        <div className="relative shrink-0">
                          <SquarePlayer
                            metalId={player.metalId}
                            versionId={player.versionId}
                            playerUrl={player.playerUrl}
                            hasDynamicImage={player.hasDynamicImage}
                            className={cn(isMobile && "size-14 rounded-none")}
                          />
                          {isMobile && (
                            <span className="absolute right-0 bottom-0 min-w-6 rounded-tl bg-neutral-950/85 px-1 text-center text-xs leading-4.5 font-bold text-neutral-50 tabular-nums">
                              {player.rating}
                            </span>
                          )}
                          {isSelecting && (
                            <span
                              className={cn(
                                "absolute flex size-4 items-center justify-center rounded-full border",
                                isMobile
                                  ? "top-1 right-1"
                                  : "-right-1 -bottom-1",
                                isSelected
                                  ? "border-primary bg-primary text-on-primary"
                                  : "border-neutral-500 bg-neutral-800 text-transparent",
                              )}
                            >
                              <Check className="size-3" strokeWidth={3} />
                            </span>
                          )}
                        </div>
                        <div className="flex min-w-0 flex-col gap-1">
                          <span className="truncate font-medium text-neutral-100 mobile:font-semibold">
                            {player.displayName}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <img
                              src={`${ASSETS}/${GAME_ASSET_SLUG}/clubs/dark/${player.clubId}.png`}
                              alt={t("alt.flag")}
                              className="h-4"
                            />
                            <img
                              src={`${ASSETS}/${GAME_ASSET_SLUG}/countries/${player.countryId}.png`}
                              alt={t("alt.flag")}
                              className="h-3"
                            />
                          </span>
                        </div>
                      </div>
                    </td>
                    {/* Figures are right-aligned so their place values line up. */}
                    <td className="px-2 py-2 mobile:px-3">
                      <span className="flex items-center justify-end gap-1 font-oswald text-sm font-medium text-neutral-50 tabular-nums mobile:text-base">
                        {iconUrl && (
                          <img
                            className="size-3.5 shrink-0 mobile:size-4"
                            src={iconUrl}
                            alt={t("alt.price")}
                          />
                        )}
                        {label && <span>{label}</span>}
                        {showNumber && (
                          <span>
                            {convertPlayerPrice(displayPrice, isMobile)}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right mobile:px-3">
                      <span
                        className={cn(
                          "font-oswald text-sm font-medium tabular-nums mobile:text-base",
                          !player.profit
                            ? "text-foreground-muted"
                            : player.profit > 0
                              ? "text-upgrade"
                              : "text-negative-foreground",
                        )}
                      >
                        {player.profit
                          ? `${player.profit > 0 ? "+" : ""}${convertPlayerPrice(player.profit, isMobile)}`
                          : "–"}
                      </span>
                    </td>
                    <td className="py-2 pr-4 pl-2 text-right mobile:px-3">
                      {player.itemScore !== undefined ? (
                        <span className="flex items-center justify-end gap-1 font-oswald text-sm font-medium text-neutral-300 tabular-nums mobile:text-base mobile:text-neutral-50">
                          <ItemScoreDiamond size={isMobile ? 11 : 14} />
                          {isMobile
                            ? convertSBCPrice(player.itemScore)
                            : player.itemScore.toLocaleString("en-US")}
                        </span>
                      ) : (
                        <span className="text-foreground-muted">–</span>
                      )}
                    </td>
                    <td className="hidden px-3 py-2 text-neutral-300 tabular-nums mobile:table-cell">
                      {player.rating}
                    </td>
                    <td className="hidden px-3 py-2 text-foreground-muted mobile:table-cell">
                      {positions[player.position]?.name ?? player.position}
                    </td>
                    <td className="hidden px-3 py-2 mobile:table-cell">
                      <div className="flex items-center gap-1">
                        {PROPERTIES.filter(({ key }) => player[key]).map(
                          ({ key, icon }) => (
                            <Tooltip
                              key={key}
                              content={t(`players.property.${key}`)}
                              side="bottom"
                              delayDuration={100}
                            >
                              <img
                                className="size-5 cursor-default"
                                src={`${ASSETS}/sbc-icons/${icon}.png`}
                                alt=""
                              />
                            </Tooltip>
                          ),
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {totalAmountOfPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalAmountOfPages}
          onChange={setPage}
          className="self-end px-4 py-4 mobile:px-6"
        />
      )}
    </div>
  );
};
